import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Container, Title, Text } from '@mantine/core';
import { ArrowRight, DownloadSimple, BookOpen } from '@phosphor-icons/react';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import api from '../utils/api';

function EbookCard({ ebook }) {
  const { t } = useTranslation();
  return (
    <Link
      to={`/ebooki/${ebook.slug}`}
      className="group flex flex-col bg-white rounded-2xl border border-gray-200 overflow-hidden hover:border-indigo-300 hover:shadow-lg transition-all duration-200"
    >
      <div className="relative bg-gray-50 border-b border-gray-100 p-5 flex items-center justify-center">
        <img
          src={ebook.cover}
          alt={ebook.title}
          loading="lazy"
          className="w-full max-w-[210px] rounded-md shadow-md group-hover:scale-[1.02] transition-transform duration-200"
          style={{ aspectRatio: '210 / 297', objectFit: 'cover' }}
        />
      </div>
      <div className="flex flex-col flex-1 p-5">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-indigo-600 mb-1.5">
          {ebook.platform}
        </span>
        <h3 className="text-[17px] font-semibold text-gray-900 leading-snug mb-2">
          {ebook.title}
        </h3>
        <p className="text-sm text-gray-500 leading-relaxed flex-1">{ebook.blurb}</p>
        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 mt-4 group-hover:gap-2.5 transition-all">
          {t('ebooks.downloadFree')}
          <ArrowRight size={16} weight="bold" />
        </span>
      </div>
    </Link>
  );
}

export default function EbooksPage() {
  const { t } = useTranslation();
  const [ebooks, setEbooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/accounts/ebooks/')
      .then((r) => setEbooks(r.data.ebooks || []))
      .catch(() => setEbooks([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#F6F5F4]">
      <SEO
        title={t('ebooks.seoTitle')}
        description={t('ebooks.seoDescription')}
        path="/ebooki"
      />
      <Navbar />

      <section className="pt-28 pb-12">
        <Container size="xl">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-indigo-600 bg-indigo-50 rounded-full px-3 py-1.5 mb-5">
              <BookOpen size={15} weight="duotone" />
              {t('ebooks.badge')}
            </span>
            <Title order={1} fz={{ base: 30, md: 46 }} mb="md" style={{ letterSpacing: '-0.02em' }}>
              {t('ebooks.heroTitle')}
            </Title>
            <Text c="dimmed" fz={{ base: 15, md: 18 }} maw={680} mx="auto" lh={1.6}>
              {t('ebooks.heroSubtitle')}
            </Text>
          </div>
        </Container>
      </section>

      <section className="pb-24">
        <Container size="xl">
          {loading ? (
            <Text ta="center" c="dimmed">{t('ebooks.loading')}</Text>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
              {ebooks.map((e) => (
                <EbookCard key={e.slug} ebook={e} />
              ))}
            </div>
          )}

          <div className="max-w-6xl mx-auto mt-10">
            <div className="rounded-2xl bg-indigo-600 text-white px-6 py-8 md:px-10 md:py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
              <div>
                <h2 className="text-xl md:text-2xl font-semibold mb-1.5">
                  {t('ebooks.ctaTitle')}
                </h2>
                <p className="text-indigo-100 text-sm md:text-base max-w-xl leading-relaxed">
                  {t('ebooks.ctaText')}
                </p>
              </div>
              <Link
                to="/rejestracja"
                className="shrink-0 inline-flex items-center gap-2 bg-white text-indigo-700 font-semibold rounded-xl px-5 py-3 hover:bg-indigo-50 transition"
              >
                <DownloadSimple size={18} weight="bold" />
                {t('ebooks.trialButton')}
              </Link>
            </div>
          </div>
        </Container>
      </section>

      <Footer />
    </div>
  );
}
